import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IBuyCard from "components/displays/iBuyCard";
import OutlinedChip from "components/displays/outlinedChip";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import GradientDivider from "styled/styledComponents/GradientDivider";
import { openSmartFormModal } from "utils/smartFormsUtils";

import { NON_CATALOGUE_ITEM } from "../../constants/generalConstants";
import {
    PRODUCT_DETAILS_PAGE,
    SMART_FORM_DETAILS_PAGE,
} from "../../constants/routes";
import axiosApiInstance from "../../services/middleware/axios";
import { getImg } from "../../services/search/searchAPIs";
import { SMART_FORM_SEARCH_BY_ID } from "../../services/search/urls";
import alertDispatcher from "../../utils/alertsUtils";
import AddToCartButton from "../buttons/addToCartButton";
import FavouriteButton from "../buttons/favouriteButton";
import CurrencyFigure from "../displays/currencyFigure";
import noImage from "./../../assets/noImage.png";

const DESCRIPTION_WRAP_CHARACTERS = 75;

const ProductCard = ({ product, isDesktop }) => {
    const [imgUrl, setImgUrl] = useState(noImage);
    const [isSmartFormLoading, setIsSmartFormLoading] = useState(false);
    const isSmartForm = !!product?.TEMPLATE_ID;
    const browsingOU = useSelector((state) => state.auth.activeOU);
    const dispatch = useDispatch();

    const description = isSmartForm
        ? product?.TEMPLATE_NAME
        : product?.ITEM_DESCRIPTION;

    const handleAddSmartForm = () => {
        setIsSmartFormLoading(true);
        if (product?.itemType === "SMART_FORM")
            axiosApiInstance
                .get(SMART_FORM_SEARCH_BY_ID(product?.id))
                .then((response) => {
                    const data = response.data;
                    openSmartFormModal(data, dispatch, browsingOU);
                    setIsSmartFormLoading(false);
                })
                .catch((e) => {
                    setIsSmartFormLoading(false);
                    alertDispatcher(
                        "smartFormDetails",
                        e?.response?.status,
                        dispatch
                    );
                });
    };

    useEffect(() => {
        if (isSmartForm) {
            getImg(product?.IMAGE, setImgUrl);
        } else getImg(product?.ITEM_IMAGE_URL, setImgUrl);
    }, [product]);

    const disableNonCatItemClick = () =>
        product?.itemType == NON_CATALOGUE_ITEM
            ? (e) => e.preventDefault()
            : null;

    const isDescriptionWrapped = () =>
        description ? description.length > DESCRIPTION_WRAP_CHARACTERS : false;

    const descriptionText = () =>
        isDescriptionWrapped()
            ? description.substring(0, DESCRIPTION_WRAP_CHARACTERS) + "..."
            : description;

    return (
        <Grid
            container
            flexDirection="column"
            sx={{ marginTop: isDesktop ? "24px" : "0px" }}
        >
            <Link
                to={
                    isSmartForm
                        ? SMART_FORM_DETAILS_PAGE(product?.TEMPLATE_ID)
                        : PRODUCT_DETAILS_PAGE(product?.PO_LINE_ID)
                }
                style={{ textDecoration: "none" }}
                onClick={disableNonCatItemClick}
            >
                <IBuyCard
                    xs={12}
                    additionalSx={{
                        margin: isDesktop ? "0px" : "4px 10px",
                        overflow: "visible",
                    }}
                >
                    <Grid
                        container
                        item
                        xs={12}
                        sx={{
                            display: "flex",
                            flexDirection: isDesktop ? "column" : "row",
                            paddingTop: "1px",
                        }}
                    >
                        <Grid
                            container
                            item
                            xs={isDesktop ? 12 : 5}
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0px",
                                position: "relative",
                                ":hover": {
                                    transform: "scale(1.03)",
                                    transition: "0.2s ease",
                                },
                            }}
                        >
                            <Grid
                                item
                                alt={description}
                                alignItems="center"
                                alignContent="center"
                                component="img"
                                src={imgUrl}
                                aria-label={description}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null; // prevents looping
                                    currentTarget.src = noImage;
                                }}
                                sx={{
                                    border: isSmartForm
                                        ? "4px solid green"
                                        : null,
                                    borderRadius: "10px",
                                    marginTop: isDesktop ? "-24px" : "0px",
                                    boxShadow: "10",
                                    ":hover": {
                                        boxShadow: "16",
                                    },
                                }}
                                mr={isDesktop ? 4 : 0}
                                ml={isDesktop ? 4 : 2}
                                width={isDesktop ? "73%" : "85%"}
                                height="auto"
                            />
                            <FavouriteButton
                                product={isSmartForm ? null : product}
                                smartForm={isSmartForm ? product : null}
                                activeOU={browsingOU}
                                positionAbsolute
                            />
                        </Grid>
                        <Grid container item xs={isDesktop ? 12 : 7}>
                            <Fade in={true} timeout={200}>
                                <CardContent sx={{ width: "100%" }}>
                                    <Tooltip
                                        disableInteractive
                                        title={
                                            isDescriptionWrapped()
                                                ? description
                                                : ""
                                        }
                                    >
                                        <Typography
                                            fontWeight="bold"
                                            variant="body2"
                                        >
                                            {descriptionText()}
                                        </Typography>
                                    </Tooltip>
                                    {isSmartForm && (
                                        <>
                                            <Typography
                                                fontWeight="bold"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {`Contract: ${
                                                    product?.CONTRACT ||
                                                    "No Associated Contract"
                                                }`}
                                            </Typography>
                                            <Typography
                                                fontWeight="bold"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {`Category: ${
                                                    product?.PURCHASING_CATEGORY ||
                                                    "No Associated Category"
                                                }`}
                                            </Typography>
                                            <Typography
                                                fontWeight="bold"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {`UoM: ${
                                                    product?.UNIT_OF_MEASURE ||
                                                    ""
                                                }`}
                                            </Typography>
                                        </>
                                    )}
                                    <Typography
                                        fontWeight="bold"
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {product?.SUPPLIER_NAME
                                            ? product.SUPPLIER_NAME
                                            : "Supplier not listed"}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            fontWeight="bold"
                                            variant="h6"
                                            color="text.subheader"
                                            sx={{
                                                margin: "8px 0px 8px 0px",
                                            }}
                                        >
                                            <CurrencyFigure
                                                value={
                                                    isSmartForm
                                                        ? 1
                                                        : product?.UNIT_PRICE
                                                }
                                                code={
                                                    isSmartForm
                                                        ? product?.CURRENCY
                                                        : product?.CURRENCY_CODE
                                                }
                                            />
                                        </Typography>
                                        {!isSmartForm && (
                                            <Typography
                                                color="text.subheader"
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{
                                                    margin: "8px 0px 8px 0px",
                                                }}
                                            >
                                                {product?.UNIT_OF_MEASURE}
                                            </Typography>
                                        )}
                                    </Box>
                                    {product?.itemType ==
                                        NON_CATALOGUE_ITEM && (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <OutlinedChip value="Non-Catalogue Item" />
                                        </Box>
                                    )}
                                    {isDesktop && (
                                        <GradientDivider
                                            sx={{
                                                margin: "8px 5px 16px 5px",
                                            }}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isSmartForm ? (
                                            <LoadingButton
                                                loading={isSmartFormLoading}
                                                variant="contained"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    handleAddSmartForm();
                                                }}
                                            >
                                                LAUNCH SMART FORM
                                            </LoadingButton>
                                        ) : (
                                            <AddToCartButton
                                                product={product}
                                                nonCat={false}
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Fade>
                        </Grid>
                    </Grid>
                </IBuyCard>
            </Link>
        </Grid>
    );
};

ProductCard.propTypes = {
    product: PropTypes.object,
};

export default ProductCard;
